import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import MapboxMiniMap from '../components/maps/MapboxMiniMap';
import {
    ArrowLeftIcon,
    MapIcon,
    DocumentTextIcon,
    ChartBarIcon,
    ExclamationTriangleIcon,
    CubeIcon,
    CheckCircleIcon,
    ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import { API_URL } from '../api/config';
import { apiGet } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatPhone, safeFormatDate } from '../utils/formatters';



type TabKey = 'overview' | 'maps' | 'work' | 'traceability' | 'issues' | 'training' | 'financials' | 'documents' | 'timeline';

const normalizeBoundary = (raw: any): [number, number][] => {
    if (!raw) return [];
    try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((point: any) => (Array.isArray(point)
                ? [Number(point[0]), Number(point[1])]
                : [Number(point.lat ?? point.latitude), Number(point.lng ?? point.longitude)]) as [number, number])
            .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));
    } catch {
        return [];
    }
};


const featureIsOn = (features: any, key: string) => {
    const f = features?.[key];
    return f ? f.enabled !== false && f.is_enabled !== false : false;
};

const resolvePhotoUrl = (value: any): string => {
    if (!value) return '/images/default-avatar.svg';
    if (value.startsWith('http')) return value;
    return `${API_URL}/${String(value).replace(/^\//, '')}`;
};

const FarmerProfilePage: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [farmer, setFarmer] = useState<any>(null);
    const [diagnostics, setDiagnostics] = useState<any>(null);
    const [farmerFields, setFarmerFields] = useState<any[]>([]);
    const [farmerTrees, setFarmerTrees] = useState<any[]>([]);
    const [assignments, setAssignments] = useState<any[]>([]);
    const [reports, setReports] = useState<any[]>([]);
    const [workspaceSummary, setWorkspaceSummary] = useState<any>({});
    const [attendance, setAttendance] = useState<any>(null);
    const [financials, setFinancials] = useState<any>(null);
    const [timeline, setTimeline] = useState<any[]>([]);
    const [documents, setDocuments] = useState<any[]>([]);
    const [issues, setIssues] = useState<any[]>([]);
    const [activeTab, setActiveTab] = useState<TabKey>('overview');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [workspaceError, setWorkspaceError] = useState('');

    const features = user?.tenant_features;
    const isSuperOrPm = user?.role === 'super_admin' || user?.role === 'project_manager';
    const canTraining = isSuperOrPm || featureIsOn(features, 'training');
    const canFinancials = isSuperOrPm || featureIsOn(features, 'vsla') || featureIsOn(features, 'sales');
    const canDocuments = isSuperOrPm || featureIsOn(features, 'documents') || featureIsOn(features, 'document_management');
    const canIssues = isSuperOrPm || featureIsOn(features, 'issues');

    const TABS: { key: TabKey; label: string; icon?: React.ReactNode }[] = [
        { key: 'overview', label: 'Overview' },
        { key: 'maps', label: 'Farms & Maps', icon: <MapIcon className="w-4 h-4" /> },
        { key: 'work', label: 'Work & Reports', icon: <ClipboardDocumentCheckIcon className="w-4 h-4" /> },
        { key: 'traceability', label: 'Traceability', icon: <CubeIcon className="w-4 h-4" /> },
        ...(canIssues ? [{ key: 'issues' as TabKey, label: 'Issues', icon: <ExclamationTriangleIcon className="w-4 h-4" /> }] : []),
        ...(canTraining ? [{ key: 'training' as TabKey, label: 'Training' }] : []),
        ...(canFinancials ? [{ key: 'financials' as TabKey, label: 'Financials', icon: <ChartBarIcon className="w-4 h-4" /> }] : []),
        ...(canDocuments ? [{ key: 'documents' as TabKey, label: 'Documents', icon: <DocumentTextIcon className="w-4 h-4" /> }] : []),
        { key: 'timeline', label: 'Timeline' },
    ];

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError('');
            setWorkspaceError('');
            try {
                const token = localStorage.getItem('aaywa_token');
                const headers: HeadersInit = token ? { 'Authorization': `Bearer ${token}` } : {};

                const farmerRes = await fetch(`${API_URL}/api/farmers/${id}`, { headers });
                if (!farmerRes.ok) {
                    const err = await farmerRes.json().catch(() => ({}));
                    throw new Error(err.error || 'Failed to load farmer');
                }
                const farmerData = await farmerRes.json();

                const [profileData, attendanceData, financialData, timelineData, documentData, workspaceData] = await Promise.all([
                    apiGet<any>(`/api/farmers/${id}/profile`).catch(() => null),
                    apiGet<any>(`/api/farmers/${id}/attendance`).catch(() => null),
                    apiGet<any>(`/api/farmers/${id}/financials`).catch(() => null),
                    apiGet<any>(`/api/farmers/${id}/timeline`).catch(() => ({ events: [] })),
                    apiGet<any>(`/api/documents?entity_type=farmer&entity_id=${id}`).catch(() => ({ documents: [] })),
                    apiGet<any>(`/api/farmers/${id}/workspace`).catch((workspaceRequestError) => ({
                        _error: workspaceRequestError?.message || 'Operational farmer data is temporarily unavailable',
                    })),
                ]);

                setFarmer(profileData ? { ...farmerData, ...profileData } : farmerData);
                setAttendance(attendanceData);
                setFinancials(financialData);
                setTimeline(timelineData?.events || []);
                setDocuments(documentData?.documents || []);
                setWorkspaceError(workspaceData?._error || '');
                setIssues(workspaceData?.issues || []);
                setFarmerFields(workspaceData?.fields || []);
                setFarmerTrees(workspaceData?.trees || []);
                setAssignments(workspaceData?.assignments || []);
                setReports(workspaceData?.reports || []);
                setWorkspaceSummary(workspaceData?.summary || {});

                if (isSuperOrPm) {
                    const diag = await apiGet<any>(`/api/farmers/${id}/assignment-diagnostics`).catch(() => null);
                    setDiagnostics(diag);
                }
            } catch (err: any) {
                setError(err.message || 'Failed to load farmer profile');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id, isSuperOrPm]);

    const coordinates = useMemo<[number, number]>(() => {
        const firstMappedField = farmerFields.find((field) => Number.isFinite(Number(field.center_lat)) && Number.isFinite(Number(field.center_lng)));
        if (firstMappedField) return [Number(firstMappedField.center_lat), Number(firstMappedField.center_lng)];
        if (!farmer?.location_coordinates) return [-1.9441, 30.0619];
        if (typeof farmer.location_coordinates === 'object') {
            const lat = Number(farmer.location_coordinates.lat);
            const lng = Number(farmer.location_coordinates.lng);
            if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
        }
        if (typeof farmer.location_coordinates === 'string') {
            try {
                const parsed = JSON.parse(farmer.location_coordinates);
                const lat = Number(parsed.lat);
                const lng = Number(parsed.lng);
                if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
            } catch {
                // ignore
            }
        }
        return [-1.9441, 30.0619];
    }, [farmer?.location_coordinates, farmerFields]);

    const fieldBoundaries = useMemo(() => farmerFields
        .map((field) => ({ field, positions: normalizeBoundary(field.boundary_coordinates) }))
        .filter(({ positions }) => positions.length >= 3), [farmerFields]);

    const hectares = Number(workspaceSummary.hectares ?? farmer?.plot_size?.hectares ?? farmer?.plot_size_hectares ?? 0);

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-slate-50">
                <div className="flex items-center gap-3 text-slate-500">
                    <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-emerald-600" />
                    Loading farmer profile...
                </div>
            </div>
        );
    }

    if (error || !farmer) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
                <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
                    <p className="font-bold text-red-700">{error || 'Farmer not found'}</p>
                    <Link to="/dashboard/farmers" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-black text-white">
                        <ArrowLeftIcon className="h-4 w-4" /> Back to farmers
                    </Link>
                </div>
            </div>
        );
    }

    const mobileLinked = Boolean(farmer.user_id || farmer.mobile_login_email);

    return (
        <div className="min-h-screen bg-slate-50">
            <div className="mx-auto max-w-7xl p-4 sm:p-6">
                <button
                    onClick={() => navigate('/dashboard/farmers')}
                    className="mb-4 inline-flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-slate-900"
                >
                    <ArrowLeftIcon className="h-4 w-4" /> Back to farmers
                </button>

                {/* Header */}
                <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                        <img
                            src={resolvePhotoUrl(farmer.photo_url || farmer.profile_url)}
                            alt={farmer.full_name}
                            onError={(e) => { e.currentTarget.src = '/images/default-avatar.svg'; }}
                            className="h-20 w-20 rounded-2xl border-4 border-white object-cover shadow"
                        />
                        <div className="flex-1 min-w-0">
                            <h1 className="text-2xl font-black text-slate-900">{farmer.full_name}</h1>
                            <p className="mt-1 text-sm text-slate-500">{formatPhone(farmer.phone)}</p>
                            <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                <span className="rounded-full bg-slate-100 px-2.5 py-1 font-bold text-slate-600">
                                    {(user as any)?.tenant?.name || (user as any)?.tenant_name || 'Workspace'}
                                </span>
                                <span className={`rounded-full px-2.5 py-1 font-bold ${(farmer.is_active ?? farmer.status) === false ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                                    {(farmer.is_active ?? farmer.status) === false ? 'Inactive' : 'Active'}
                                </span>
                                <span className={`rounded-full px-2.5 py-1 font-bold ${mobileLinked ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>
                                    {mobileLinked ? (farmer.mobile_login_active === false ? 'Mobile access disabled' : 'Mobile access: linked') : 'No mobile access yet'}
                                </span>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Link
                                to={`/dashboard/farmers`}
                                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 hover:bg-slate-50"
                            >
                                Edit
                            </Link>
                        </div>
                    </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-6">
                    {[
                        ['Fields', workspaceSummary.fields ?? farmerFields.length],
                        ['Mapped area', `${hectares.toFixed(2)} ha`],
                        ['Trees', workspaceSummary.trees ?? farmerTrees.length],
                        ['Open issues', workspaceSummary.open_issues ?? issues.filter((item) => !['closed', 'resolved'].includes(String(item.status || '').toLowerCase())).length],
                        ['Open work', workspaceSummary.open_assignments ?? assignments.length],
                        ['Reports', workspaceSummary.submitted_reports ?? reports.length],
                    ].map(([label, value]) => (
                        <div key={String(label)} className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                            <p className="text-xs font-bold text-slate-500">{label}</p>
                            <p className="mt-1 text-xl font-black text-slate-950">{value}</p>
                        </div>
                    ))}
                </div>

                {workspaceError && (
                    <div className="mt-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
                        <div>
                            <p className="font-black">Some operational data could not be loaded</p>
                            <p className="mt-0.5 text-amber-800">{workspaceError}. Refresh this profile after the API is available.</p>
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className="mt-4 flex flex-wrap gap-2 rounded-3xl border border-slate-200 bg-white p-3 shadow-sm">
                    {TABS.map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-sm font-bold transition ${activeTab === tab.key
                                ? 'bg-slate-950 text-white'
                                : 'text-slate-600 hover:bg-slate-100'}`}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="mt-4">
                    {activeTab === 'overview' && (
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                <p className="text-xs font-black uppercase tracking-wider text-slate-400">Assigned Agronomist</p>
                                <p className="mt-1 font-bold text-slate-900">
                                    {farmer?.assigned_staff?.agronomist?.full_name
                                        || diagnostics?.farmer?.farmer_agronomist_name
                                        || farmerFields[0]?.assigned_agronomist_name
                                        || 'Not assigned'}
                                </p>
                                <p className="mt-2 text-xs text-slate-500">
                                    {farmerFields.length > 0
                                        ? `Field assignments: ${farmerFields.length} field${farmerFields.length === 1 ? '' : 's'}, ${farmerTrees.length} tree${farmerTrees.length === 1 ? '' : 's'}`
                                        : diagnostics ? `Field assignments: ${(diagnostics.mapped_fields || []).length} farms, ${(diagnostics.mapped_trees || []).length} trees` : ''}
                                </p>
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                <p className="text-xs font-black uppercase tracking-wider text-slate-400">Field Facilitator</p>
                                <p className="mt-1 font-bold text-slate-900">
                                    {farmer?.assigned_staff?.field_facilitator?.full_name
                                        || diagnostics?.farmer?.farmer_field_facilitator_name
                                        || farmerFields[0]?.assigned_field_facilitator_name
                                        || 'Not assigned'}
                                </p>
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                <p className="text-xs font-black uppercase tracking-wider text-slate-400">Household Type</p>
                                <p className="mt-1 font-bold capitalize text-slate-900">{(farmer.household_type || 'n/a').replace('_', ' ')}</p>
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                <p className="text-xs font-black uppercase tracking-wider text-slate-400">Date of Birth</p>
                                <p className="mt-1 font-bold text-slate-900">{farmer.date_of_birth ? safeFormatDate(farmer.date_of_birth) : 'Not provided'}</p>
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                <p className="text-xs font-black uppercase tracking-wider text-slate-400">Address</p>
                                <p className="mt-1 font-bold text-slate-900">{farmer.location_address || farmer.address || 'Not provided'}</p>
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                <p className="text-xs font-black uppercase tracking-wider text-slate-400">Farm Size</p>
                                <p className="mt-1 font-bold text-slate-900">{hectares.toFixed(2)} ha</p>
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                <p className="text-xs font-black uppercase tracking-wider text-slate-400">Crops</p>
                                <p className="mt-1 font-bold text-slate-900">{farmer.crops || 'No crops listed'}</p>
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                <p className="text-xs font-black uppercase tracking-wider text-slate-400">Mobile Portal</p>
                                {mobileLinked ? (
                                    <p className="mt-1 font-bold text-emerald-700">{farmer.mobile_login_email || 'Phone OTP account'}</p>
                                ) : (
                                    <p className="mt-1 font-bold text-amber-700">Not linked yet</p>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'maps' && (
                        <div className="space-y-4">
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                <h3 className="flex items-center gap-2 text-lg font-black text-slate-900">
                                    <MapIcon className="h-5 w-5 text-emerald-600" />
                                    Mapped farms and farmer location
                                </h3>
                                <p className="mt-1 text-sm text-slate-500">
                                    {fieldBoundaries.length > 0
                                        ? `${fieldBoundaries.length} of ${farmerFields.length} assigned fields have saved boundaries.`
                                        : 'No assigned field has a saved boundary yet. Create it in Farm Maps.'}
                                    {farmer.location_address || farmer.address ? ` Address: ${farmer.location_address || farmer.address}.` : ''}
                                </p>
                                <div className="mt-4 h-80 overflow-hidden rounded-2xl border border-slate-200">
                                    <MapboxMiniMap
                                        center={coordinates}
                                        zoom={15}
                                        basemap="streets"
                                        style={{ height: '100%', width: '100%' }}
                                        fitBounds={fieldBoundaries.length > 0}
                                        polygons={fieldBoundaries.map(({ field, positions }: any) => ({
                                            id: field.id,
                                            coordinates: positions,
                                            color: '#047857',
                                            fillColor: '#10b981',
                                            fillOpacity: 0.22,
                                            label: field.name || field.plot_code || `Field #${field.id}`,
                                        }))}
                                        markers={fieldBoundaries.length === 0 ? [{ id: 'farmer', position: coordinates, color: '#047857', label: farmer.full_name }] : []}
                                    />
                                </div>
                            </div>
                            {farmerFields.length > 0 && (
                                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                    <h3 className="text-lg font-black text-slate-900">Fields ({farmerFields.length})</h3>
                                    <div className="mt-3 overflow-hidden rounded-xl border border-slate-200">
                                        <table className="w-full text-sm">
                                            <thead className="bg-slate-50">
                                                <tr>
                                                    <th className="p-3 text-left">Field</th>
                                                    <th className="p-3 text-left">Crop</th>
                                                    <th className="p-3 text-left">Area</th>
                                                    <th className="p-3 text-left">Trees</th>
                                                    <th className="p-3 text-left">Open issues</th>
                                                    <th className="p-3 text-left">Agronomist</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {farmerFields.map((field: any) => (
                                                    <tr key={field.id} className="border-t border-slate-100 hover:bg-slate-50">
                                                        <td className="p-3 font-semibold text-slate-900">{field.name || field.plot_code || `Field #${field.id}`}</td>
                                                        <td className="p-3 capitalize">{field.crop_type || '—'}</td>
                                                        <td className="p-3">{field.area_hectares ? `${field.area_hectares} ha` : '—'}</td>
                                                        <td className="p-3">{field.tree_count ?? field.mapped_tree_count ?? '—'}</td>
                                                        <td className="p-3">{field.open_issue_count ?? 0}</td>
                                                        <td className="p-3">{field.assigned_agronomist_name || 'Unassigned'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            {farmerFields.length === 0 && (
                                <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
                                    <MapIcon className="mx-auto h-10 w-10 text-slate-300" />
                                    <p className="mt-2 text-sm font-bold text-slate-600">No fields assigned yet</p>
                                    <p className="mt-1 text-xs text-slate-400">Fields will appear here once assigned in Farm Maps.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'work' && (
                        <div className="grid gap-4 xl:grid-cols-2">
                            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <h3 className="text-lg font-black text-slate-900">Activity assignments</h3>
                                        <p className="mt-1 text-sm text-slate-500">Field work assigned directly to this farmer.</p>
                                    </div>
                                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-700">{assignments.length}</span>
                                </div>
                                {assignments.length > 0 ? (
                                    <div className="mt-4 space-y-2">
                                        {assignments.map((assignment) => (
                                            <div key={assignment.id} className="rounded-xl border border-slate-200 p-4">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div>
                                                        <p className="text-sm font-black text-slate-900">{assignment.title || assignment.activity_type_name || 'Field activity'}</p>
                                                        <p className="mt-1 text-xs text-slate-500">{assignment.assignment_code || `Assignment #${assignment.id}`} · {assignment.field_name || assignment.plot_code || 'Field not set'}</p>
                                                    </div>
                                                    <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-bold capitalize text-slate-700">{String(assignment.status || 'draft').replaceAll('_', ' ')}</span>
                                                </div>
                                                <p className="mt-3 text-xs text-slate-500">Due {assignment.due_date ? safeFormatDate(assignment.due_date) : 'date not set'} · {assignment.priority || 'normal'} priority</p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="mt-4 rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">No activity assignments for this farmer.</p>
                                )}
                            </section>

                            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <h3 className="text-lg font-black text-slate-900">Submitted reports</h3>
                                        <p className="mt-1 text-sm text-slate-500">Evidence and outcomes submitted from field work.</p>
                                    </div>
                                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-700">{reports.length}</span>
                                </div>
                                {reports.length > 0 ? (
                                    <div className="mt-4 space-y-2">
                                        {reports.map((report) => (
                                            <div key={report.id} className="rounded-xl border border-slate-200 p-4">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div>
                                                        <p className="text-sm font-black text-slate-900">{report.assignment_title || report.report_code || 'Activity report'}</p>
                                                        <p className="mt-1 text-xs text-slate-500">{report.report_code || `Report #${report.id}`} · {report.field_name || report.plot_code || 'Field not set'}</p>
                                                    </div>
                                                    <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-bold capitalize text-emerald-700">{String(report.status || 'submitted').replaceAll('_', ' ')}</span>
                                                </div>
                                                <p className="mt-3 line-clamp-2 text-xs text-slate-600">{report.result_summary || 'No result summary recorded.'}</p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="mt-4 rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">No reports submitted by this farmer.</p>
                                )}
                            </section>
                        </div>
                    )}

                    {activeTab === 'traceability' && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                            <h3 className="flex items-center gap-2 text-lg font-black text-slate-900">
                                <CubeIcon className="h-5 w-5 text-emerald-600" />
                                Traceability ({farmerTrees.length} trees)
                            </h3>
                            {farmerTrees.length > 0 ? (
                                <div className="mt-3 overflow-hidden rounded-xl border border-slate-200">
                                    <table className="w-full text-sm">
                                        <thead className="bg-slate-50">
                                            <tr>
                                                <th className="p-3 text-left">Tree Code</th>
                                                <th className="p-3 text-left">Crop</th>
                                                <th className="p-3 text-left">Health</th>
                                                <th className="p-3 text-left">Field</th>
                                                <th className="p-3 text-left">Last Inspection</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {farmerTrees.slice(0, 50).map((tree: any) => (
                                                <tr key={tree.id} className="border-t border-slate-100 hover:bg-slate-50">
                                                    <td className="p-3 font-semibold text-slate-900">{tree.tree_code}</td>
                                                    <td className="p-3 capitalize">{tree.crop_type || '—'}</td>
                                                    <td className="p-3">
                                                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold ${
                                                            tree.health_status === 'healthy' ? 'bg-emerald-50 text-emerald-700' :
                                                            tree.health_status === 'stressed' ? 'bg-amber-50 text-amber-700' :
                                                            tree.health_status === 'critical' ? 'bg-red-50 text-red-700' :
                                                            'bg-slate-100 text-slate-600'
                                                        }`}>
                                                            {tree.health_status || 'Unknown'}
                                                        </span>
                                                    </td>
                                                    <td className="p-3 text-slate-600">{tree.plot_name || tree.plot_code || '—'}</td>
                                                    <td className="p-3 text-slate-500 text-xs">{tree.last_inspection_at ? new Date(tree.last_inspection_at).toLocaleDateString() : 'Never'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="mt-4 rounded-2xl border border-dashed border-slate-300 p-8 text-center">
                                    <CubeIcon className="mx-auto h-10 w-10 text-slate-300" />
                                    <p className="mt-2 text-sm font-bold text-slate-600">No trees mapped yet</p>
                                    <p className="mt-1 text-xs text-slate-400">Trees will appear here once added in Traceability.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'issues' && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                            <h3 className="flex items-center gap-2 text-lg font-black text-slate-900">
                                <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
                                Issues & Reports
                            </h3>
                            {issues.length > 0 ? (
                                <div className="mt-3 space-y-2">
                                    {issues.map((issue: any) => (
                                        <div key={issue.id} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                                            <div className="flex items-center justify-between gap-2">
                                                <p className="text-sm font-bold text-slate-900">{issue.title || issue.category || 'Farmer issue'}</p>
                                                <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-bold capitalize text-slate-600">{issue.status || 'open'}</span>
                                            </div>
                                            <p className="mt-1 text-xs text-slate-500">{issue.description || 'No description'}</p>
                                            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                                                <span>{issue.field_name || issue.plot_code || 'No field linked'}</span>
                                                {issue.tree_code && <span>Tree {issue.tree_code}</span>}
                                                <span>Reported {safeFormatDate(issue.created_at)}</span>
                                                {issue.urgency && <span className="font-bold capitalize">{issue.urgency} urgency</span>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="mt-2 text-sm text-slate-500">No issues reported for this farmer.</p>
                            )}
                        </div>
                    )}

                    {activeTab === 'training' && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                            <h3 className="text-lg font-black text-slate-900">Training Attendance</h3>
                            <p className="mt-2 text-sm text-slate-600">
                                Attendance rate: <span className="font-black text-slate-900">{attendance?.summary?.attendance_rate ?? 0}%</span>
                                {' '}({attendance?.summary?.attended_sessions ?? 0}/{attendance?.summary?.total_sessions ?? 0} sessions)
                            </p>
                            {(attendance?.sessions || []).length > 0 ? (
                                <div className="mt-3 overflow-hidden rounded-xl border border-slate-200">
                                    <table className="w-full text-sm">
                                        <thead className="bg-slate-50">
                                            <tr>
                                                <th className="p-3 text-left">Session</th>
                                                <th className="p-3 text-left">Date</th>
                                                <th className="p-3 text-left">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(attendance?.sessions || []).map((session: any) => (
                                                <tr key={session.id} className="border-t border-slate-100">
                                                    <td className="p-3">{session.title}</td>
                                                    <td className="p-3">{safeFormatDate(session.session_date)}</td>
                                                    <td className="p-3 capitalize">{session.attendance_status}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p className="mt-2 text-sm text-slate-500">No training sessions recorded yet.</p>
                            )}
                        </div>
                    )}

                    {activeTab === 'financials' && (
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                <p className="text-xs font-black uppercase tracking-wider text-slate-400">VSLA Savings</p>
                                <p className="mt-1 text-2xl font-black text-slate-900">{formatCurrency(financials?.vsla?.total_savings || 0)}</p>
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                <p className="text-xs font-black uppercase tracking-wider text-slate-400">Total Income from Sales</p>
                                <p className="mt-1 text-2xl font-black text-slate-900">{formatCurrency(financials?.sales?.farmer_income_total || 0)}</p>
                            </div>
                        </div>
                    )}

                    {activeTab === 'documents' && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                            <h3 className="flex items-center gap-2 text-lg font-black text-slate-900">
                                <DocumentTextIcon className="h-5 w-5 text-emerald-600" />
                                Documents
                            </h3>
                            {documents.length > 0 ? (
                                <div className="mt-3 space-y-2">
                                    {documents.map((doc: any) => (
                                        <a
                                            key={doc.id}
                                            href={doc.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="block rounded-xl border border-slate-100 bg-slate-50 p-3 text-sm font-bold text-slate-800 hover:bg-slate-100"
                                        >
                                            {doc.title || doc.filename || `Document #${doc.id}`}
                                        </a>
                                    ))}
                                </div>
                            ) : (
                                <p className="mt-2 text-sm text-slate-500">No documents uploaded for this farmer.</p>
                            )}
                        </div>
                    )}

                    {activeTab === 'timeline' && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                            <h3 className="text-lg font-black text-slate-900">Farmer Timeline</h3>
                            {timeline.length > 0 ? (
                                <div className="mt-3 space-y-3">
                                    {timeline.map((event: any, index: number) => (
                                        <div key={event.id || index} className="flex gap-3">
                                            <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
                                            <div>
                                                <p className="text-sm font-bold text-slate-900">{event.title || event.type || 'Event'}</p>
                                                <p className="text-xs text-slate-500">{safeFormatDate(event.created_at || event.date)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="mt-2 text-sm text-slate-500">No timeline activity yet.</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FarmerProfilePage;
